import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, ProfileContext }  from '../../App';
import { useTranslation } from 'react-i18next';
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, renderColumnHeader, TableProps, useReactTable } from '../Grid';
import { formatDate } from '../../util/Format';
import {get} from '../../util/HttpRequest'
import Waiting from '../Waiting';
import GridSummary from '../GridSummary';
import GridPagination from '../GridPagination';
import {formatAddressOneLine, formatPhone} from '../../util/Format';

class ClaimInspectionData {
    claimInspectionId: string = "";
    inspectionRequestDateUTC: string = "";
    inspectionCompletionDateUTC: string|null = null;
    vendorName: string = "";
    internationalCode: string = "";
    areaCode: string = "";
    phoneNumber: string = "";
    extension: string = "";
    addressLine1: string = "";
    addressLine2: string = "";
    city: string = "";
    state: string = "";
    postalCode: string = "";
    countryISO: string = "";
};

class ClaimInspectionsData {
    recordCount: number = 0;
    refreshDate: Date = new Date();
    searchResults: ClaimInspectionData[] = [];
};

function ClaimInspections(props) {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [gridData, setGridData] = useState(new ClaimInspectionsData());
    const {accessToken, extendSession, user} = useContext(AuthContext);
    const {userProfileLossDraftsClaim} = useContext(ProfileContext);
    const { t } = useTranslation(["ClaimInspections", "DataGrid"]);
    // Props used by renderColumnHeader() from Grid.tsx to control header UI:
    // Includes sorting labels and optional class names for styling
    const columnHeaderProps = [
        {
            id: 'inspectionRequestDateUTC', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'inspectionCompletionDateUTC', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'vendorName', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'address', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'phone', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        }
    ];

    useEffect (() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfileLossDraftsClaim.claimId]);

    function refreshData() {
        setLoading(true);
        getData();
    };
    
    function getData() {
        extendSession();
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimInspections");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());

        console.log("Fetch Claim Contractors from " + url);

        get(url, accessToken)
        .then( response => {
            if (!response.ok) { throw response }
            return response.json() as Promise<ClaimInspectionData[]>
        })
        .then( searchResults => {
            const data = {
              recordCount: searchResults.length, 
              refreshDate: new Date(),
              searchResults: searchResults
            };
            setGridData(data);
            setErrorMsg("");
            setLoading(false);
        })
        .catch( error => {
            setGridData(new ClaimInspectionsData());
            setErrorMsg(error.message);
            setLoading(false);
        })
    };

    function Table<TData extends object>({ columns, data }: TableProps<TData>) {
         const pageSize = Number(import.meta.env.VITE_GRID_PAGE_SIZE);
        // Use the state and functions returned from useReactTable to build your UI
        const table = useReactTable({
            data,
            columns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(), //load client-side pagination code
            getSortedRowModel: getSortedRowModel(),
            initialState: {
                pagination: {
                    pageIndex: 0, // Start on first page
                    pageSize: pageSize,
                },
            }, 
        });

        const filters = <></>;

        const pagination = (
            <GridPagination
                canPreviousPage={table.getCanPreviousPage()}
                canNextPage={table.getCanNextPage()}
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                gotoPage={table.setPageIndex}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
            />
        );

        const summary = (
            <GridSummary
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                pageSize={pageSize}
                recordCount={data.length}
                recordType={t('gridRecordType', { count: data.length })}
                refreshDate={gridData.refreshDate}
                pagination={pagination}
            />
        );

        return (
            <>
                {filters}
                <br />
                {summary}
                <table className="table" role="table">
                    <thead role="rowgroup">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} role="row">
                                {headerGroup.headers.map((header) =>
                                    renderColumnHeader(header, columnHeaderProps.find(x => x.id === header.column.id))
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody role="rowgroup">
                        {table.getRowModel().rows.map((row, i) => (
                            <React.Fragment key={row.id}>
                                <tr className={"row-" + (i % 2 === 0 ? "even" : "odd")} role="row" tabIndex={0}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} role="cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </>
        )
    }

    function renderDataGrid() {
        const columns = [{
            header: () => (<>{t('columnRequestDate')}</>),
            accessorKey: 'inspectionRequestDateUTC',
            enableSorting: true,
            cell: (info) => (
                <div className="datagrid-data-left">
                {
                    formatDate(info.getValue())
                }
                </div>
            )
        },  {
            header: () => (<>{t('columnCompletionDate')}</>),
            accessorKey:'inspectionCompletionDateUTC',
            enableSorting: true,
            cell: (info) => (
                <div className="datagrid-data-left">
                {
                    info.getValue() ? formatDate(info.getValue()) : ''
                }
                </div>
            )
        }, {
            header: () => (<>{t('columnCompany')}</>),
            accessorKey: 'vendorName',
            enableSorting: true,
        }, {
            id: 'address',
            header: () => (<>{t('columnAddress')}</>),
            accessorFn: (row) => {
                return formatAddressOneLine([row.addressLine1, row.addressLine2, row.city, row.state, row.postalCode, row.countryISO]);
            },
            enableSorting: true,
        },  {
            id: 'phone',
            header: () => (<>{t('columnPhone')}</>),
            accessorFn: (row) => {
                return formatPhone(row.internationalCode, row.areaCode, row.phoneNumber, row.extension);
            },
            enableSorting: true,
        }];

        const table = <Table
            columns={columns}
            data={gridData.searchResults} 
        />;

        return <div>{table}</div>;
    };

    const title = <h2>{t('title')}</h2>;
    const contents = (errorMsg > '') ? <p><em>{errorMsg}</em></p> : loading ? <Waiting size='large' /> : renderDataGrid();
    return <div>{title}<br />{contents}</div>
};

export default ClaimInspections;